import { ThemeProvider } from '@forgeglobal/design-framework';
import { StyledEngineProvider } from '@mui/material';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from 'react-query';
import { BrowserRouter } from 'react-router-dom';

import SecurityProvider, {
  getOktaAuth,
} from '@tcs/client-portal-common/providers/SecurityProvider';
import App from '@tcs/client-portal/App';
import { loadConfig } from '@tcs/client-portal/config/configService';
import { AnalyticsProvider } from '@tcs/common/hooks/analytics';
import { LoggerProvider } from '@tcs/common/hooks/logger';
import { queryClient } from '@tcs/common/hooks/useQuery';
import theme from '@tcs/common/theme';

import ConfigProvider from '@tcs/client-portal-common/providers/ConfigProvider';
import '@tcs/client-portal/assets/css/fonts.css';
import ErrorBoundary from '@tcs/common/components/atoms/ErrorBoundary';

const createAnalyticsConfig = (config) => {
  return {
    googleAnalyticsTrackingId: config.GOOGLE_ANALYTICS_TRACKING_ID,
    heapAppId: config.HEAP_APP_ID,
    appInsightsInstrumentationKey: config.APPINSIGHTS_INSTRUMENTATIONKEY,
    logRocketAppId: config.LOGROCKET_APP_ID,
    segmentWriteKey: config.SEGMENT_WRITE_KEY,
  };
};

(async () => {
  const config = await loadConfig();

  // Initialize launch darkly as generic forgeclient user
  // once logged in, we call .identify in SessionProvider to set the real username
  const user = { key: 'forgeclient', anonymous: true };
  const FeatureFlagProvider = await asyncWithLDProvider({
    clientSideID: config.LAUNCHDARKLY_CLIENT_ID,
    user,
  });

  const oktaAuth = getOktaAuth();

  const analyticsConfig = createAnalyticsConfig(config);

  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );

  root.render(
    <ConfigProvider config={config}>
      <BrowserRouter basename={config.REACT_APP_BASE_PATH}>
        <SecurityProvider oktaAuth={oktaAuth}>
          <QueryClientProvider
            client={queryClient(oktaAuth, config.API_BASE_URL)}>
            <AnalyticsProvider config={analyticsConfig}>
              <LoggerProvider>
                <FeatureFlagProvider>
                  <StyledEngineProvider injectFirst>
                    <ThemeProvider themeCustomization={theme}>
                      <ErrorBoundary>
                        <App />
                      </ErrorBoundary>
                    </ThemeProvider>
                  </StyledEngineProvider>
                </FeatureFlagProvider>
              </LoggerProvider>
            </AnalyticsProvider>
          </QueryClientProvider>
        </SecurityProvider>
      </BrowserRouter>
    </ConfigProvider>
  );
})();
